import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import { MdCall } from "react-icons/md";

export default function CallTransactionDetailsModal({ isOpen, transaction, onClose }) {
  if (!isOpen || !transaction) return null;

  const {
    callType,
    durationMinutes,
    coinsPerMinute,
    status,
    callerId = {},
    receiverId = {},
    totalCoins = 0,
    adminCommissionAmount = 0,
    createdAt,
    callStartTime,
    callEndTime,
    callerBalanceBefore,
    callerBalanceAfter,
    receiverBalanceBefore,
    receiverBalanceAfter,
  } = transaction;

  const durationText = durationMinutes ? `${Math.round(durationMinutes)}m` : "-";
  const adminCommissionPct = totalCoins ? `${((adminCommissionAmount / totalCoins) * 100).toFixed(1)}%` : "-";
  const receiverEarnings = (totalCoins || 0) - (adminCommissionAmount || 0);

  const formatTs = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-[var(--black)]"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-3xl bg-[var(--black)] text-[var(--yellow)] rounded-xl shadow-xl border-2 border-[var(--yellow)] overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "rgba(255, 198, 0, 0.15)" }}>
          <div className="flex items-center gap-3">
            <span className="text-2xl"><MdCall /></span>
            <h3 className="text-lg font-semibold text-[var(--yellow)]">Call Transaction Details</h3>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-[var(--black)] border border-[var(--yellow)] text-[var(--yellow)] hover:bg-[var(--yellow)] hover:text-[var(--black)] transition"
            aria-label="Close"
          >
            <AiOutlineClose />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="p-5 overflow-y-auto" style={{ maxHeight: "calc(90vh - 80px)" }}>
          {/* Top row: Call Info + Participants */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <h4 className="font-medium mb-3 text-[var(--yellow)]">Call Information</h4>
              <div className="text-sm text-[color:var(--yellow)/0.95] space-y-2">
                <div>
                  <strong className="text-[color:var(--yellow)/0.95]">Type:</strong>
                  <span className="ml-2 text-[color:var(--yellow)/0.95]">
                    {callType ? callType.charAt(0).toUpperCase() + callType.slice(1) : "-"}
                  </span>
                </div>

                <div>
                  <strong className="text-[color:var(--yellow)/0.95]">Duration:</strong>
                  <span className="ml-2">{durationText}</span>
                </div>

                <div>
                  <strong className="text-[color:var(--yellow)/0.95]">Rate:</strong>
                  <span className="ml-2">{coinsPerMinute ? `${coinsPerMinute} coins/min` : "-"}</span>
                </div>

                <div className="mt-2">
                  <strong className="text-[color:var(--yellow)/0.95]">Status:</strong>
                  <span className="ml-2 inline-block px-3 py-1 rounded-full bg-[var(--yellow)] text-[var(--black)] text-sm">
                    {status ? (status.charAt(0).toUpperCase() + status.slice(1)) : "-"}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3 text-[var(--yellow)]">Participants</h4>

              <div className="text-sm text-[color:var(--yellow)/0.95] space-y-4">
                <div>
                  <div className="text-xs text-[color:var(--yellow)/0.8]">Caller:</div>
                  <div className="font-semibold text-[var(--yellow)]">{callerId.fullName || callerId.name || callerId.email || "-"}</div>
                  {callerId.email && <div className="text-xs text-[color:var(--yellow)/0.75]">{callerId.email}</div>}
                  {callerId.phoneNumber && <div className="text-xs text-[color:var(--yellow)/0.75]">{callerId.phoneNumber}</div>}
                </div>

                <div>
                  <div className="text-xs text-[color:var(--yellow)/0.8]">Receiver:</div>
                  <div className="font-semibold text-[var(--yellow)]">{receiverId.fullName || receiverId.name || receiverId.email || "-"}</div>
                  {receiverId.email && <div className="text-xs text-[color:var(--yellow)/0.75]">{receiverId.email}</div>}
                  {receiverId.phoneNumber && <div className="text-xs text-[color:var(--yellow)/0.75]">{receiverId.phoneNumber}</div>}
                </div>
              </div>
            </div>
          </div>

          <h1 className="text-[var(--yellow)] mb-3">Financial Details</h1>

          {/* Financial cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-[var(--black)] border border-[color:var(--yellow)] rounded-lg">
              <div className="text-sm text-[color:var(--yellow)/0.85]">Total Coins</div>
              <div className="mt-2 text-xl font-bold text-[var(--yellow)]">{totalCoins}</div>
            </div>

            <div className="p-4 bg-[var(--black)] border border-[color:var(--yellow)] rounded-lg">
              <div className="text-sm text-[color:var(--yellow)/0.85]">Admin Commission</div>
              <div className="mt-2 text-xl font-bold text-[var(--yellow)]">
                {adminCommissionAmount} <span className="text-sm font-normal text-[color:var(--yellow)]">({adminCommissionPct})</span>
              </div>
            </div>

            <div className="p-4 bg-[var(--black)] border border-[color:var(--yellow)] rounded-lg">
              <div className="text-sm text-[color:var(--yellow)/0.85]">Receiver Earnings</div>
              <div className="mt-2 text-xl font-bold text-[var(--yellow)]">{receiverEarnings}</div>
            </div>
          </div>

          {/* Balance changes */}
          <div className="mb-4">
            <h4 className="font-medium mb-3 text-[var(--yellow)]">Balance Changes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-[color:var(--yellow)]">
              <div>
                <div className="text-xs text-[color:var(--yellow)/0.75]">Caller Balance</div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="text-sm">Before:</div>
                  <div className="font-medium">{callerBalanceBefore ?? 0}</div>
                  <div className="text-sm">→</div>
                  <div className="font-medium">{callerBalanceAfter ?? "-"}</div>
                </div>
              </div>

              <div>
                <div className="text-xs text-[var(--yellow)]">Receiver Balance</div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="text-sm">Before:</div>
                  <div className="font-medium">{receiverBalanceBefore ?? 0}</div>
                  <div className="text-sm">→</div>
                  <div className="font-medium">{receiverBalanceAfter ?? "-"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="text-sm text-[color:var(--yellow)]">
            <div>Created: {formatTs(createdAt)}</div>
            <div>Call Start: {formatTs(callStartTime)}</div>
            <div>Call End: {formatTs(callEndTime)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
